import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/* hs-eslint ignored failing-rules */

/* eslint-disable promise/catch-or-return */
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { checkout } from 'self-service-api/core/utilities/links';
import styled from 'styled-components';
import UIContactSalesButton from 'ui-addon-upgrades/button/UIContactSalesButton';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import * as tracker from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import UIFloatingAlertList from 'UIComponents/alert/UIFloatingAlertList';
import UIButton from 'UIComponents/button/UIButton';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import { omit } from '../../utils';
import { getProducts } from '../common/api/getProducts';
import AdminOnlyWrapper from '../common/components/AdminOnlyWrapper';
import { REQUEST_SKU } from '../common/constants/AdminModalTypes';
import { BILLING } from '../common/constants/AdminTypes';
import { upgradeDataPropsInterface } from '../common/data/upgradeData/interfaces/upgradeDataPropsInterface';
import { getCanCheckoutRecurringSkusAsIncrease } from '../common/eligibility/getCanCheckoutRecurringSkusAsIncrease';
import { navigateTo } from '../common/navigation/url';
import logPurchaseOrderSentry from '../common/reliability/logCheckoutError';
import logError from '../common/reliability/logError';
import { getIsMultipleOfferQuantityUpgrade, getProductTrackingName } from '../utils/productUtils';
import CheckoutFloatingAlertStore from './CheckoutFloatingAlertStore';
import { CheckoutButtonPropsInterface } from './CheckoutPropsInterface';
import { generateMultipleOfferPurchaseOrder } from './generatePurchaseOrder';
const StyledContactSalesButton = styled(UIContactSalesButton).withConfig({
  displayName: "MultipleOfferCheckoutButton__StyledContactSalesButton",
  componentId: "rhveow-0"
})(["display:none !important;"]);

const MultipleOfferCheckoutButton = (_ref) => {
  let {
    upgradeData,
    offerProductConfigurations = [],
    nonRecurringProductConfigurations = [],
    currencyCode,
    productBasedOffers = [],
    onClick = () => {},
    onError = () => {},
    children,
    beforeRedirect = () => Promise.resolve(true),
    products = null,
    shouldShowErrorAlert = false,
    isUpgradePoint,
    disabled = false
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["upgradeData", "offerProductConfigurations", "nonRecurringProductConfigurations", "currencyCode", "productBasedOffers", "onClick", "onError", "children", "beforeRedirect", "products", "shouldShowErrorAlert", "isUpgradePoint", "disabled"]);

  const [productList, setProductList] = useState(products);
  const [canCheckoutRecurringSkusAsIncrease, setCanCheckoutRecurringSkusAsIncrease] = useState(undefined);
  const [lastOrderGenerationFailed, setLastOrderGenerationFailed] = useState(false);
  const [isGeneratingPurchaseOrder, setIsGeneratingPurchaseOrder] = useState(false);
  const [isFetchingProducts, setIsFetchingProducts] = useState(!productList);
  const [hasError, setHasError] = useState(false);
  const [trackingProperties, setTrackingProperties] = useState({});
  const allProductConfigurations = [...offerProductConfigurations.map(({
    productConfigurations
  }) => productConfigurations).flat(), ...nonRecurringProductConfigurations];
  const skuIds = allProductConfigurations.map(({
    skuId
  }) => skuId);
  const offerIds = offerProductConfigurations.map(({
    offerId
  }) => offerId).flat();

  const getCheckoutButtonInteractionTrackingProperties = () => {
    const productNames = skuIds.map(skuId => {
      return getProductTrackingName(productList, skuId);
    });
    const isQuantityUpgrade = getIsMultipleOfferQuantityUpgrade(productList, offerProductConfigurations);
    return {
      currencyCode,
      productBasedOfferIds: productBasedOffers,
      isQuantityUpgrade,
      checkoutType: 'full',
      skuIds,
      productNames,
      offerIds
    };
  };

  useEffect(() => {
    if (productList && (offerProductConfigurations.length || nonRecurringProductConfigurations.length)) {
      setTrackingProperties(Object.assign({}, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint), {}, getCheckoutButtonInteractionTrackingProperties()));
      tracker.track('checkoutButtonInteraction', Object.assign({
        action: 'viewed'
      }, getUpgradeDataTrackingProperties(upgradeData, isUpgradePoint), {}, getCheckoutButtonInteractionTrackingProperties()));
    } else {
      setIsFetchingProducts(true);
      getProducts().then(fetchedProducts => {
        setIsFetchingProducts(false);
        setProductList(fetchedProducts);
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [productList, offerProductConfigurations, nonRecurringProductConfigurations]);
  useEffect(() => {
    if (typeof canCheckoutRecurringSkusAsIncrease === 'undefined') {
      getCanCheckoutRecurringSkusAsIncrease().then(res => setCanCheckoutRecurringSkusAsIncrease(res)).catch(() => setCanCheckoutRecurringSkusAsIncrease(false));
    }
  }, [canCheckoutRecurringSkusAsIncrease]);

  const onOrderGenerationStart = e => {
    setLastOrderGenerationFailed(false);
    setIsGeneratingPurchaseOrder(true);
    tracker.trackBeforeUnload('checkoutButtonInteraction', Object.assign({
      action: 'clicked'
    }, trackingProperties));

    if (onClick) {
      onClick(e);
    }
  };

  const onOrderGenerationComplete = () => {
    setLastOrderGenerationFailed(false);
    setIsGeneratingPurchaseOrder(false);
    tracker.trackBeforeUnload('checkoutButtonInteraction', Object.assign({
      action: 'purchase order generated'
    }, trackingProperties));
  };

  const onOrderGenerationFailed = err => {
    if (onError) {
      onError(err);
    }

    setLastOrderGenerationFailed(true);
    setIsGeneratingPurchaseOrder(false);
    tracker.track('checkoutButtonInteraction', Object.assign({
      action: 'purchase order generation failed'
    }, trackingProperties));
    logError('generatePurchaseOrder', err, Object.assign({}, trackingProperties));
    logPurchaseOrderSentry('generatePurchaseOrder', err, Object.assign({
      buttonName: 'MultipleOfferCheckoutButton'
    }, trackingProperties));

    if (shouldShowErrorAlert) {
      setHasError(true);
      CheckoutFloatingAlertStore.addAlert({
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.buyNowFailureAlert.title"
        }),
        message: /*#__PURE__*/_jsxs(UIButtonWrapper, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "upgrades.buyNowFailureAlert.message"
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: () => {
              CheckoutFloatingAlertStore.resetStore();
              document.getElementById('hidden-contact-sales-button').click();
            },
            use: "tertiary",
            size: "extra-small",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "upgrades.contactSales"
            })
          })]
        }),
        type: 'danger'
      });
    }
  };

  const handleClick = e => {
    if (!productList) return;
    onOrderGenerationStart(e);
    generateMultipleOfferPurchaseOrder(currencyCode, offerProductConfigurations, nonRecurringProductConfigurations, productList, productBasedOffers, canCheckoutRecurringSkusAsIncrease).then(orderId => {
      onOrderGenerationComplete();
      const checkoutUrl = checkout(orderId, upgradeData && upgradeData.upgradeSource ? upgradeData.upgradeSource : ''); // eslint-disable-next-line @typescript-eslint/no-floating-promises

      beforeRedirect(checkoutUrl).then((shouldRedirect = true) => shouldRedirect && navigateTo(checkoutUrl));
    }).catch(onOrderGenerationFailed);
  };

  const propsToPass = omit(rest, ['allowModal', 'shouldShowErrorAlert', 'products', 'isUpgradePoint', 'level', 'trackingSource', 'data-selenium-id', 'canViewNewSeats']);
  const originalId = rest['data-selenium-id'];
  const idToUse = isFetchingProducts ? `${originalId}-disabled` : originalId;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasError && shouldShowErrorAlert && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(StyledContactSalesButton, {
        upgradeData: Object.assign({}, upgradeData, {
          uniqueId: 'checkout-error'
        }),
        id: "hidden-contact-sales-button"
      }), /*#__PURE__*/_jsx(UIFloatingAlertList, {
        alertStore: CheckoutFloatingAlertStore
      })]
    }), /*#__PURE__*/_jsx(AdminOnlyWrapper, {
      upgradeData: upgradeData,
      adminType: BILLING,
      modalType: REQUEST_SKU,
      children: /*#__PURE__*/_jsx(UILoadingButton, Object.assign({}, propsToPass, {
        failed: lastOrderGenerationFailed,
        loading: isGeneratingPurchaseOrder,
        disabled: isFetchingProducts || disabled,
        onClick: handleClick,
        "data-selenium": "checkout-button",
        "data-reagan-primary-cta": true,
        "data-selenium-id": idToUse,
        children: children || /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'upgrades.checkoutButton.default'
        })
      }))
    })]
  });
};

MultipleOfferCheckoutButton.defaultProps = {
  use: 'primary'
};
MultipleOfferCheckoutButton.propTypes = Object.assign({}, upgradeDataPropsInterface, {}, omit(CheckoutButtonPropsInterface, 'productConfigurations', 'offerId'), {
  //remove these when checkout button is fully migrated
  children: PropTypes.node
});
export default MultipleOfferCheckoutButton;